/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import { useContext, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../customs/DiviPage/PageHeader/PageHeader';
import { DiviNavigationIds } from '../../../models/diviProgress';
import PageFooter from '../../customs/DiviPage/PageFooter/PageFooter';
import DiviPage from '../../customs/DiviPage/DiviPage';
import { NodeType } from '../../../backendModels/report.model';
import { getPageTitle, PageType } from '../../../models/diviPage';
import { BaseNodeRecordValueTypeMap } from '../../../backendModels/records.model';
import PatientData from './PatientData/PatientData';
import style from './PatientMasterData.scss?inline';
import { useCSS } from '../../../provider/CSSProvider';
import Age from './Age/Age';
import Gender from './Gender/Gender';
import Bmi from './Bmi/Bmi';
import Mission from './Mission/Mission';
import { Typography } from '@mui/material';
import {
  BirthDateBackendFormat,
  EXTENDED_PATIENT_DATA_RECORD_ELEMENT_KEY,
  ExtendedPatientDataRecord,
  getAgeFromBirthday,
} from '../../../models/genericElements/extendedPatientData';
import { ReportsAPIContext } from '../../../provider/ReportsAPIProvider';
import { Draft } from 'immer';
import { InputState } from '../../../backendModels/general.model';
import { isGenericRecordDeletable } from '../../../models/generic';
import {
  CONTROL_CENTER_RECORD_ELEMENT_KEY,
  ControlCenterPropertyKeys,
  ControlCenterRecord,
} from '../../../models/genericElements/controlCenter';
import {
  PATIENT_DATA_RECORD_ELEMENT_KEY,
  PatientDataRecord,
  PatientPropertyKeys,
} from '../../../models/genericElements/patientData';
import {
  handleUntouchedNumericFieldUpdates,
  handleUntouchedSingleSelectFieldUpdates,
  handleUntouchedTextFieldUpdates,
  overwriteNumericField,
  overwriteSingleSelectField,
  overwriteTextField,
} from '../../../utils/recordWithTouched';
import { SavePatient } from './SavePatient';
import dayjs from 'dayjs';
import _ from 'lodash';
import { DataImportDialog } from '../../customs/ImportDialog/DataImportDialog';
import { localizePatientPropertyTuple, NormalizedPatient, normalizePatient } from '../../../models/patient';
import { localizeOperationalPropertyTuple, NormalizedMission, normalizeMissionData } from '../../../models/operational';
import {
  calculateMissionDataDiff,
  calculatePatientDiff,
  compareByDiviOrder,
  controlCenterPropertiesOfMissionDataForPatientMaster,
} from './PatientMasterData.functions';
import { findPatientDataDiff, hasAnyMissionDataDiff, hasAnyPatientDataDiff } from '../../../utils/dataDiff';
import { ApplicationConfig } from '../../../config/application.config';

export function PatientMasterData() {
  useCSS(style);
  const recordTypesOfPage = [] as (keyof BaseNodeRecordValueTypeMap)[];
  const nodeTypeOfPage = NodeType.METADATA;
  const elementKeysOfPage = [
    PATIENT_DATA_RECORD_ELEMENT_KEY,
    EXTENDED_PATIENT_DATA_RECORD_ELEMENT_KEY,
    CONTROL_CENTER_RECORD_ELEMENT_KEY,
  ];

  const {
    findNodes,
    adaptRecord,
    patient,
    missionData,
    hidePatientSaveButton,
    findRecordOrDefault,
    lastSynchronizedBaseData,
    setLastSynchronizedBaseData,
    lastSynchronizedMissionData,
    setLastSynchronizedMissionData,
  } = useContext(ReportsAPIContext);

  const patientRecord = findRecordOrDefault(
    'generic',
    nodeTypeOfPage,
    PATIENT_DATA_RECORD_ELEMENT_KEY
  ) as PatientDataRecord;

  const extendedPatientRecord = findRecordOrDefault(
    'generic',
    nodeTypeOfPage,
    EXTENDED_PATIENT_DATA_RECORD_ELEMENT_KEY
  ) as ExtendedPatientDataRecord;

  const controlCenterRecord = findRecordOrDefault(
    'generic',
    nodeTypeOfPage,
    CONTROL_CENTER_RECORD_ELEMENT_KEY
  ) as ControlCenterRecord;

  const patientDataDiff = useMemo(
    () => calculatePatientDiff(patient, lastSynchronizedBaseData),
    [patient, lastSynchronizedBaseData]
  );

  const missionDataDiff = useMemo(
    () => calculateMissionDataDiff(missionData, lastSynchronizedMissionData),
    [missionData, lastSynchronizedMissionData]
  );

  const localizedDataDiff = useMemo(() => {
    patientDataDiff.sort(compareByDiviOrder);
    missionDataDiff.sort(compareByDiviOrder);
    const patientDiff = patientDataDiff.map((val) => {
      return localizePatientPropertyTuple(val[0] as keyof NormalizedPatient, val[1], {
        patientRecord,
        extendedPatientRecord,
      });
    });

    const missionDiff = missionDataDiff.map((val) => {
      return localizeOperationalPropertyTuple(val[0] as keyof NormalizedMission, val[1], {
        controlCenterRecord,
      });
    });

    return [...patientDiff, ...missionDiff];
  }, [patientDataDiff, patientRecord, extendedPatientRecord, missionDataDiff, controlCenterRecord]);

  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  useEffect(() => {
    setIsImportDialogOpen(patientDataDiff.length > 0 || missionDataDiff.length > 0);
  }, [patientDataDiff, missionDataDiff]);

  useEffect(() => {
    function savePatient(onSuccess: (normalizedPatient: NormalizedPatient) => void) {
      if (patient != null && findNodes(nodeTypeOfPage).length > 0) {
        const birthday = patient.birthday != null ? dayjs(patient.birthday, BirthDateBackendFormat) : null;
        const [age, newbornAge] = getAgeFromBirthday(birthday);

        // We only want to change birthday, age and newbornAge together.
        // Therefore, we do not change any one of them if one is touched or already has a content.
        const isBirthdayAndAgeEmptyAndUntouched =
          patientRecord.values?.birthday?.text == null &&
          patientRecord.values?.age?.number == null &&
          extendedPatientRecord.values?.newbornAge?.option == null &&
          !patientRecord.values?.touched?.options?.includes(PatientPropertyKeys.birthday) &&
          !patientRecord.values?.touched?.options?.includes(PatientPropertyKeys.age);

        adaptRecord(
          'generic',
          nodeTypeOfPage,
          (draft: Draft<PatientDataRecord>, deleteRecord) => {
            draft.inputState = InputState.ENTERED;

            const synchronizedDataUpdate: NormalizedPatient = {};

            handleUntouchedTextFieldUpdates(
              draft,
              PatientPropertyKeys.patientId,
              patient.patientId,
              synchronizedDataUpdate
            );
            handleUntouchedTextFieldUpdates(draft, PatientPropertyKeys.name, patient.name, synchronizedDataUpdate);
            handleUntouchedTextFieldUpdates(
              draft,
              PatientPropertyKeys.surname,
              patient.surname,
              synchronizedDataUpdate
            );
            handleUntouchedTextFieldUpdates(draft, PatientPropertyKeys.street, patient.street, synchronizedDataUpdate);
            handleUntouchedTextFieldUpdates(
              draft,
              PatientPropertyKeys.postalCode,
              patient.postalCode,
              synchronizedDataUpdate
            );
            handleUntouchedTextFieldUpdates(draft, PatientPropertyKeys.city, patient.city, synchronizedDataUpdate);
            handleUntouchedTextFieldUpdates(
              draft,
              PatientPropertyKeys.insuranceNumber,
              patient.insuranceNumber,
              synchronizedDataUpdate
            );
            handleUntouchedTextFieldUpdates(
              draft,
              PatientPropertyKeys.insuranceName,
              patient.insuranceName,
              synchronizedDataUpdate
            );
            handleUntouchedTextFieldUpdates(
              draft,
              PatientPropertyKeys.policyNumber,
              patient.policyNumber,
              synchronizedDataUpdate
            );
            handleUntouchedSingleSelectFieldUpdates(
              draft,
              PatientPropertyKeys.gender,
              patient.gender,
              synchronizedDataUpdate
            );

            if (isBirthdayAndAgeEmptyAndUntouched) {
              handleUntouchedTextFieldUpdates(
                draft,
                PatientPropertyKeys.birthday,
                patient.birthday,
                synchronizedDataUpdate
              );
              delete draft.values?.age;
              handleUntouchedNumericFieldUpdates(
                draft,
                PatientPropertyKeys.age,
                age ?? patient.age,
                synchronizedDataUpdate
              );
              synchronizedDataUpdate.newbornAge = newbornAge;
            } else {
              if ((patient.birthday ?? null) === (draft.values?.birthday?.text ?? null)) {
                synchronizedDataUpdate.birthday = patient.birthday ?? null;
              }
              if ((age ?? patient.age ?? null) === (draft.values?.age?.number ?? null)) {
                synchronizedDataUpdate.age = age ?? patient.age ?? null;
              }
              if ((newbornAge ?? null) === (draft.values?.birthday?.text ?? null)) {
                synchronizedDataUpdate.newbornAge = newbornAge ?? null;
              }
            }

            if (isGenericRecordDeletable(draft)) {
              deleteRecord();
            }

            onSuccess(synchronizedDataUpdate);
          },
          PATIENT_DATA_RECORD_ELEMENT_KEY
        );

        if (isBirthdayAndAgeEmptyAndUntouched) {
          adaptRecord(
            'generic',
            nodeTypeOfPage,
            (draft: Draft<ExtendedPatientDataRecord>, deleteRecord) => {
              draft.inputState = InputState.ENTERED;

              if (newbornAge == null) {
                delete draft.values?.newbornAge;
              } else {
                draft.values = {
                  ...draft.values,
                  newbornAge: {
                    fieldType: 'singleSelect',
                    option: newbornAge,
                  },
                };
              }

              if (isGenericRecordDeletable(draft)) {
                deleteRecord();
              }
            },
            EXTENDED_PATIENT_DATA_RECORD_ELEMENT_KEY
          );
        }
      }
    }

    const updateLastSynchronizedPatient = (synchronizedDataUpdate: NormalizedPatient) => {
      setLastSynchronizedBaseData((oldData) => {
        return { patient: { ...oldData?.patient, ...synchronizedDataUpdate } };
      });
    };

    savePatient(updateLastSynchronizedPatient);
  }, [
    patient,
    adaptRecord,
    findNodes,
    nodeTypeOfPage,
    patientRecord,
    setLastSynchronizedBaseData,
    extendedPatientRecord,
    lastSynchronizedBaseData,
    missionData,
    setLastSynchronizedMissionData,
  ]);

  function importChangedPatientData() {
    const newBornAgeDiff = findPatientDataDiff(patientDataDiff, 'newbornAge');
    const hasPatientDataDiff = hasAnyPatientDataDiff(patientDataDiff, [
      PatientPropertyKeys.patientId,
      PatientPropertyKeys.name,
      PatientPropertyKeys.surname,
      PatientPropertyKeys.street,
      PatientPropertyKeys.postalCode,
      PatientPropertyKeys.city,
      PatientPropertyKeys.insuranceNumber,
      PatientPropertyKeys.insuranceName,
      PatientPropertyKeys.policyNumber,
      PatientPropertyKeys.gender,
      PatientPropertyKeys.birthday,
      PatientPropertyKeys.age,
    ]);

    if (hasPatientDataDiff) {
      adaptRecord(
        'generic',
        nodeTypeOfPage,
        (draft: Draft<PatientDataRecord>) => {
          draft.inputState = InputState.ENTERED;

          overwriteTextField(draft, PatientPropertyKeys.patientId, patient?.patientId, patientDataDiff);
          overwriteTextField(draft, PatientPropertyKeys.name, patient?.name, patientDataDiff);
          overwriteTextField(draft, PatientPropertyKeys.surname, patient?.surname, patientDataDiff);
          overwriteTextField(draft, PatientPropertyKeys.street, patient?.street, patientDataDiff);
          overwriteTextField(draft, PatientPropertyKeys.postalCode, patient?.postalCode, patientDataDiff);
          overwriteTextField(draft, PatientPropertyKeys.city, patient?.city, patientDataDiff);
          overwriteTextField(draft, PatientPropertyKeys.insuranceNumber, patient?.insuranceNumber, patientDataDiff);
          overwriteTextField(draft, PatientPropertyKeys.insuranceName, patient?.insuranceName, patientDataDiff);
          overwriteTextField(draft, PatientPropertyKeys.policyNumber, patient?.policyNumber, patientDataDiff);
          overwriteSingleSelectField(draft, PatientPropertyKeys.gender, patient?.gender, patientDataDiff);

          overwriteTextField(draft, PatientPropertyKeys.birthday, patient?.birthday, patientDataDiff);
          overwriteNumericField(
            draft,
            PatientPropertyKeys.age,
            patientDataDiff.find((item) => item[0] === PatientPropertyKeys.age)?.[1],
            patientDataDiff
          );
        },
        PATIENT_DATA_RECORD_ELEMENT_KEY
      );
    }

    if (newBornAgeDiff != null) {
      const newbornAge = newBornAgeDiff[1];

      adaptRecord(
        'generic',
        nodeTypeOfPage,
        (draft: Draft<ExtendedPatientDataRecord>, deleteRecord) => {
          draft.inputState = InputState.ENTERED;

          if (newbornAge == null) {
            delete draft.values?.newbornAge;
          } else {
            draft.values = {
              ...draft.values,
              newbornAge: {
                fieldType: 'singleSelect',
                option: newbornAge,
              },
            };
          }

          if (isGenericRecordDeletable(draft)) {
            deleteRecord();
          }
        },
        EXTENDED_PATIENT_DATA_RECORD_ELEMENT_KEY
      );
    }

    if (hasPatientDataDiff || newBornAgeDiff != null) {
      setLastSynchronizedBaseData((oldData) => {
        const newData = _.omit(normalizePatient(patient), ['symptomOnset']);
        return { patient: { ...oldData?.patient, ...newData } };
      });
    }
  }

  function importChangedMissionData() {
    if (
      !hasAnyMissionDataDiff(missionDataDiff, [
        ControlCenterPropertyKeys.controlCenter,
        ControlCenterPropertyKeys.location,
        ControlCenterPropertyKeys.personnelNumber,
      ])
    ) {
      return;
    }

    adaptRecord(
      'generic',
      nodeTypeOfPage,
      (draft: Draft<ControlCenterRecord>) => {
        draft.inputState = InputState.ENTERED;

        overwriteTextField(
          draft,
          ControlCenterPropertyKeys.controlCenter,
          missionData?.controlCenter?.controlCenter,
          missionDataDiff
        );
        overwriteTextField(
          draft,
          ControlCenterPropertyKeys.location,
          missionData?.controlCenter?.location,
          missionDataDiff
        );
        overwriteTextField(
          draft,
          ControlCenterPropertyKeys.personnelNumber,
          missionData?.controlCenter?.personnelNumber,
          missionDataDiff
        );
      },
      CONTROL_CENTER_RECORD_ELEMENT_KEY
    );

    setLastSynchronizedMissionData((oldData) => {
      const newData = _.pick(normalizeMissionData(missionData), controlCenterPropertiesOfMissionDataForPatientMaster);
      return { mission: { ...oldData?.mission, ...newData } };
    });
  }

  function importChangedPatientMasterData() {
    importChangedPatientData();
    importChangedMissionData();
  }

  function ignoreChangedPatientMasterData() {
    setLastSynchronizedBaseData((oldData) => {
      const newData = _.omit(normalizePatient(patient), ['symptomOnset']);
      return { patient: { ...oldData?.patient, ...newData } };
    });
    setLastSynchronizedMissionData((oldData) => {
      const newData = _.pick(normalizeMissionData(missionData), [
        'controlCenter',
        'controlCenterLicensePlate',
        'location',
        'personnelNumber',
      ]);
      return { mission: { ...oldData?.mission, ...newData } };
    });
  }

  const pageHeader = <PageHeader heading={getPageTitle(PageType.PatientMasterData)} noClearAllButton />;

  const pageFooter = (
    <PageFooter
      noBackButton
      pageType={PageType.PatientMasterData}
      diviId={DiviNavigationIds.Pat}
      elementKeys={elementKeysOfPage}
    />
  );

  return (
    <DiviPage
      pageHeader={pageHeader}
      pageFooter={pageFooter}
      recordTypes={recordTypesOfPage}
      nodeType={nodeTypeOfPage}
      diviId={DiviNavigationIds.Pat}
      elementKeys={elementKeysOfPage}>
      {ApplicationConfig.immediatelySyncToConference && (
        <DataImportDialog
          isDialogOpen={isImportDialogOpen}
          localizedDataDiff={localizedDataDiff}
          onConfirm={importChangedPatientMasterData}
          onAbort={ignoreChangedPatientMasterData}
        />
      )}
      <div className='legend-with-save row'>
        <div className='row'>
          <div className='indicator'></div>
          <Typography variant='subtitle1'>Automatisch übertragene Daten</Typography>
        </div>
        {!hidePatientSaveButton && !ApplicationConfig.immediatelySyncToConference && <SavePatient />}
      </div>
      <div className='patient-master-data-wireframe'>
        <div className='patient-data'>
          <PatientData nodeType={nodeTypeOfPage} />
        </div>
        <Gender nodeType={nodeTypeOfPage} />
        <Bmi nodeType={nodeTypeOfPage} />
        <Age nodeType={nodeTypeOfPage} />
        <div className='mission'>
          <Mission nodeType={nodeTypeOfPage} />
        </div>
      </div>
    </DiviPage>
  );
}
